// ========================================
// PWA UTILITIES - Service Worker & Push Notifications
// ========================================

import { VAPID_PUBLIC_KEY, isVapidConfigured, VAPID_NOT_CONFIGURED_MESSAGE } from '../config/vapid';
import { logger } from '../utils/logger';

// ========================================
// ENREGISTREMENT DU SERVICE WORKER
// ========================================

export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      logger.log('[PWA] Enregistrement du Service Worker...');
      
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });

      logger.log('[PWA] Service Worker enregistré:', registration.scope);

      // Écouter les mises à jour
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        logger.log('[PWA] Nouvelle version du Service Worker détectée');

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // Nouvelle version disponible
            logger.log('[PWA] Nouvelle version disponible');
            
            // Notifier l'utilisateur
            if (window.confirm('Une nouvelle version est disponible. Voulez-vous la charger ?')) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
              window.location.reload();
            }
          }
        });
      });

      return registration;
    } catch (error) {
      logger.error('[PWA] Erreur enregistrement Service Worker:', error);
      return null;
    }
  } else {
    logger.log('[PWA] Service Worker non supporté');
    return null;
  }
};

// ========================================
// DÉSENREGISTREMENT DU SERVICE WORKER
// ========================================

export const unregisterServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.unregister();
        logger.log('[PWA] Service Worker désenregistré');
        return true;
      }
    } catch (error) {
      logger.error('[PWA] Erreur désenregistrement:', error);
    }
  }
  return false;
};

// ========================================
// GESTION DU CACHE
// ========================================

export const clearCache = async () => {
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      logger.log('[PWA] Cache vidé');
      return true;
    } catch (error) {
      logger.error('[PWA] Erreur vidage cache:', error);
      return false;
    }
  }
  return false;
};

export const getCacheSize = async () => {
  if ('caches' in window && 'storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage,
        quota: estimate.quota,
        percentage: (estimate.usage / estimate.quota * 100).toFixed(2)
      };
    } catch (error) {
      logger.error('[PWA] Erreur estimation cache:', error);
      return null;
    }
  }
  return null;
};

// ========================================
// DÉTECTION STATUT RÉSEAU
// ========================================

export const isOnline = () => {
  return navigator.onLine;
};

export const addOnlineListener = (callback) => {
  window.addEventListener('online', callback);
  return () => window.removeEventListener('online', callback);
};

export const addOfflineListener = (callback) => {
  window.addEventListener('offline', callback);
  return () => window.removeEventListener('offline', callback);
};

// ========================================
// NOTIFICATIONS PUSH
// ========================================

export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    logger.log('[PWA] Notifications non supportées');
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    logger.log('[PWA] Permission notifications:', permission);
    return permission;
  } catch (error) {
    logger.error('[PWA] Erreur demande permission:', error);
    return null;
  }
};

export const getNotificationPermission = () => {
  if ('Notification' in window) {
    return Notification.permission;
  }
  return null;
};

export const subscribeToPushNotifications = async (registration) => {
  if (!registration) {
    logger.error('[PWA] Pas de Service Worker enregistré');
    return null;
  }

  // Vérifier que la clé VAPID est configurée
  if (!isVapidConfigured()) {
    logger.warn('[PWA] Clés VAPID non configurées');
    logger.warn(VAPID_NOT_CONFIGURED_MESSAGE);
    return null;
  }

  try {
    // Vérifier si déjà abonné
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Créer nouvel abonnement
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
      logger.log('[PWA] Abonné aux notifications push');
    } else {
      logger.log('[PWA] Déjà abonné aux notifications push');
    }

    return subscription;
  } catch (error) {
    logger.error('[PWA] Erreur abonnement push:', error);
    return null;
  }
};

export const unsubscribeFromPushNotifications = async (registration) => {
  if (!registration) {
    return false;
  }

  try {
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      logger.log('[PWA] Désabonné des notifications push');
      return true;
    }
  } catch (error) {
    logger.error('[PWA] Erreur désabonnement:', error);
  }
  return false;
};

// ========================================
// INSTALLATION PWA
// ========================================

let deferredPrompt = null;

export const setupInstallPrompt = () => {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    logger.log('[PWA] Prompt d\'installation disponible');
  });

  window.addEventListener('appinstalled', () => {
    logger.log('[PWA] Application installée');
    deferredPrompt = null;
  });
};

export const canInstall = () => {
  return deferredPrompt !== null;
};

export const promptInstall = async () => {
  if (!deferredPrompt) {
    logger.log('[PWA] Prompt d\'installation non disponible');
    return false;
  }

  try {
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    logger.log('[PWA] Résultat installation:', result.outcome);
    deferredPrompt = null;
    return result.outcome === 'accepted';
  } catch (error) {
    logger.error('[PWA] Erreur prompt installation:', error);
    return false;
  }
};

export const isInstalled = () => {
  // Vérifier si l'app est en standalone mode
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

// ========================================
// SYNCHRONISATION EN ARRIÈRE-PLAN
// ========================================

export const registerBackgroundSync = async (registration, tag = 'sync-data') => {
  if (!registration || !('sync' in registration)) {
    logger.log('[PWA] Background Sync non supporté');
    return false;
  }

  try {
    await registration.sync.register(tag);
    logger.log('[PWA] Background Sync enregistré:', tag);
    return true;
  } catch (error) {
    logger.error('[PWA] Erreur Background Sync:', error);
    return false;
  }
};

// ========================================
// UTILITAIRES
// ========================================

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// ========================================
// MESSAGES AU SERVICE WORKER
// ========================================

export const sendMessageToSW = async (message) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    try {
      navigator.serviceWorker.controller.postMessage(message);
      return true;
    } catch (error) {
      logger.error('[PWA] Erreur envoi message:', error);
      return false;
    }
  }
  return false;
};

// Écouter les messages du Service Worker
export const listenToSWMessages = (callback) => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', callback);
    return () => {
      navigator.serviceWorker.removeEventListener('message', callback);
    };
  }
  return () => {};
};

// ========================================
// INITIALISATION
// ========================================

export const initializePWA = async () => {
  logger.log('[PWA] Initialisation...');

  // Setup install prompt
  setupInstallPrompt();

  // Enregistrer Service Worker
  const registration = await registerServiceWorker();

  // Écouter messages du SW
  listenToSWMessages((event) => {
    logger.log('[PWA] Message du SW:', event.data);
    
    if (event.data && event.data.type === 'SYNC_COMPLETE') {
      logger.log('[PWA] Synchronisation terminée');
    }
  });

  return {
    registration,
    isInstalled: isInstalled(),
    canInstall: canInstall(),
    isOnline: isOnline()
  };
};

// ========================================
// EXPORT PAR DÉFAUT
// ========================================

export default {
  registerServiceWorker,
  unregisterServiceWorker,
  clearCache,
  getCacheSize,
  isOnline,
  addOnlineListener,
  addOfflineListener,
  requestNotificationPermission,
  getNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  setupInstallPrompt,
  canInstall,
  promptInstall,
  isInstalled,
  registerBackgroundSync,
  sendMessageToSW,
  listenToSWMessages,
  initializePWA
};
